import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { LoaderCircle } from '@skytech/ui'
import { useDispatch, useSelector } from 'react-redux'

import { getInventory, getDevicePolicy, getIsLoading } from '@/ducks/devicePolicyReducer'
import useUser, { useIsIntunor } from '@/hooks/user'

import { Translation } from '../i18n/Translation'

import Content from './Content'
import { createOrder } from './OrderRepository'
import type { Inventory as InventoryType, Order, OrderInfo, Product } from './types'

import './style/shop.scss'

const Shop: FC = () => {
  const dispatch = useDispatch()

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [orderInfo, setOrderInfo] = useState<OrderInfo>({
    orderStep1done: false,
    orderStep2done: false,
    orderStep3done: false,
    orderStep4done: false,
  })

  const user = useUser()
  const isIntunor = useIsIntunor()

  const inventory: InventoryType = useSelector(getInventory)
  const isLoading: boolean = useSelector(getIsLoading)

  useEffect(() => {
    if (!user) return
    // @ts-ignore
    dispatch(getDevicePolicy(user?.code))
  }, [user])

  const createCurrentOrder = (product: Product, remainder: number, downPaymentDuration: number) => {
    const order = createOrder(product, remainder, downPaymentDuration)

    setSelectedOrder(order)
    setOrderInfo(prev => ({ ...prev, orderStep2done: true }))
  }

  const getOrderStage = () => {
    const mainProduct = selectedOrder ? selectedOrder.mainProduct : null

    if (orderInfo.orderStep1done && !orderInfo.orderStep2done) {
      return 2 // device model and price
    }

    const accessoryCount =
      mainProduct?.children?.reduce(
        (acc, cur) => acc + (inventory.find(product => product.id === cur) ? 1 : 0),
        0
      ) || 0

    if (
      orderInfo.orderStep2done &&
      !orderInfo.orderStep3done &&
      mainProduct &&
      accessoryCount > 0 &&
      !isIntunor
    ) {
      return 3 // accessories (if available)
    }

    if (
      orderInfo.orderStep1done &&
      orderInfo.orderStep2done &&
      selectedOrder &&
      !selectedOrder.canceled
    ) {
      return 4 // confirmation
    }

    return 1 // inventory
  }

  return (
    <div id="shop" className="is-hidden">
      <div className="shop-content">
        <div className="header">
          <Translation id="Søk om mobil" />
        </div>

        {isLoading ? (
          <div className="flex justify-center">
            <LoaderCircle />
          </div>
        ) : (
          <Content
            order={selectedOrder}
            orderStage={getOrderStage()}
            onOrderInfoSet={(v: Partial<OrderInfo>) => setOrderInfo(prev => ({ ...prev, ...v }))}
            onOrderSet={(v: Order | null) => setSelectedOrder(v)}
            onOrderCreate={createCurrentOrder}
          />
        )}
      </div>
    </div>
  )
}

export default Shop
